// NPM packages
import styled from '@emotion/styled';
import { StoryblokComponent, useStoryblok } from '@storyblok/react';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scroller } from 'react-scroll';

// All other imports
import NotFoundContent from 'components/core/NotFoundContent';
import CircularProgress from 'components/core/CircularProgress';

const Root = styled.div({
  overflow: 'visible',
});

export interface StaticPageProps {
  className?: string;
}

// https://ericdcobb.medium.com/scrolling-to-an-anchor-in-react-when-your-elements-are-rendered-asynchronously-8c64f77b5f34
const useHashScroll = (contentLoaded: boolean): void => {
  const scrollRef = useRef(false);
  const hash = useLocation().hash;
  const hashRef = useRef(hash);

  useEffect(() => {
    if (hash && contentLoaded) {

      if (hashRef.current !== hash) {
        hashRef.current = hash;
        scrollRef.current = false;
      }

      if (!scrollRef.current) {
        const id = hash.replace('#', '');

        // Add delay to allow Storyblok content to fully render
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            scroller.scrollTo(id, {
              duration: 800,
              delay: 0,
              smooth: 'easeInOutQuart',
            });
            scrollRef.current = true;
          }
        }, 500);
      }
    }
  }, [hash, contentLoaded]);
};

export default function StaticPage(
  props: StaticPageProps
): React.ReactElement | null {
  const { className } = props;
  const webappPathname = useLocation().pathname;
  const isDraftVersion = process.env.NODE_ENV !== "production";
  const storyblokPathname = webappPathname.replace(/\/(minimal)?\//, '');


  const story = useStoryblok(storyblokPathname!, {
    version: isDraftVersion ? 'draft' : 'published',
    resolve_relations: [
      'resourceCategoryList.resourceCategories',
      'textResource.authors',
      'videoResource.authors',
    ],
  });

  const contentLoaded = !!story.content;
  useHashScroll(contentLoaded);

  let body = <CircularProgress text={'Loading...'} />;

  if (story.content) {
    // Check for not found marker from notFoundResponse() object to return NotFoundContent component for 4XX errors
    // This is a workaround for Storyblok not returning a 404 status code for non-existent pages
    const isNotFound =
      story.slug === 'page_not_found' &&
      (typeof story.content.header === 'string' && story.content.header === 'Page Not Found');

    if (isNotFound) {
      body = <NotFoundContent />;
    } else {
      body = <StoryblokComponent blok={story.content} />;
    }
  }

  return <Root className={className}>{body}</Root>;
}
